import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Input } from "antd";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import './TextInput.scss';
import { makeVar, useReactiveVar } from '@apollo/client';
import { STATES } from '../../../../../utils/constants';
import { formatAddressWithoutCountry } from '../../../../../utils/functions';
import { useDropdownScrollLock, SCROLL_FEEDBACK_CLASS } from '../../DropdownScroll';

interface TextInputProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  isPassword?: boolean;
  error?: string;
  children?: React.ReactNode;
  loading?: boolean;
  className?: string;
  floatingLabel?: boolean;
  alwaysShowFloatingLabel?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const TextInput: React.FC<TextInputProps> = (props) => {
  const { label, value, onChange, isPassword, error, children, loading, className, floatingLabel = false, alwaysShowFloatingLabel = false, disabled, placeholder } = props;

  const inputType = isPassword ? "password" : "text";
  const inputClass = `${error ? 'text-input error' : 'text-input'}${className ? ` ${className}` : ''}`;
  const [isFocused, setIsFocused] = useState(false);
  const hasContent = typeof value === 'number' ? true : Boolean(value && value.toString().trim().length > 0);
  const showFloatingLabel = Boolean(
    floatingLabel && 
    label && 
    (alwaysShowFloatingLabel || isFocused || hasContent)
  );

  return (
    <div className={inputClass}>
      <div
        className={`input-container${showFloatingLabel ? ' with-floating-label' : ''}`}
        onFocusCapture={() => floatingLabel && setIsFocused(true)}
        onBlurCapture={() => floatingLabel && setIsFocused(false)}
      >
        {showFloatingLabel && label && (
          <label className="floating-label">{label}</label>
        )}
        {
          children || (
            <input
              className="text-input-field"
              placeholder={placeholder || label}
              type={inputType}
              value={value}
              disabled={disabled}
              onChange={(event) => onChange?.(event.target.value)}
            />
          )
        }
        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
          </div>
        )}
      </div>
      {error && <p className='error-message'>{error}</p>}
    </div>
  );
};

export const investmentPropertyAddressVar = makeVar<string>("Investment property address");

interface InputAutocompleteAddressProps {
  disabled?: boolean;
  onSelect?: (data: any) => void;
  onChange?: (address: string) => void;
  value?: string;
  initialValue?: string;
  className?: string;
  error?: string;
  useNative?: boolean; // fallback to plain input (for contexts where Antd conflicts)
  excludedStates?: string[]; // Array de códigos de estado a excluir
  placeholder?: string; // Placeholder opcional
}

export const InputAutocompleteAddress: React.FC<InputAutocompleteAddressProps> = ({ disabled, onSelect, onChange, value: externalValue, initialValue, className = '', error, useNative, excludedStates = [], placeholder: placeholderProp }) => {
  const placeholderFromVar = useReactiveVar(investmentPropertyAddressVar);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useDropdownScrollLock(isDropdownOpen);
  const placeholder = placeholderProp ?? placeholderFromVar;
  
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: "us" },
      language: 'en'
    },
    debounce: 300
  });

  // Track last synced external value to avoid unnecessary updates
  const lastSyncedExternalValue = useRef<string | undefined>(undefined);

  // Sync externalValue with internal value when externalValue changes (not when user types)
  useEffect(() => {
    if (externalValue !== undefined) {
      const externalValueStr = String(externalValue);
      // Only update if the external value actually changed from outside
      if (externalValueStr !== lastSyncedExternalValue.current) {
        lastSyncedExternalValue.current = externalValueStr;
        setValue(externalValueStr, false);
      }
    } else if (externalValue === undefined && lastSyncedExternalValue.current !== undefined) {
      // Reset tracking when external value becomes undefined
      lastSyncedExternalValue.current = undefined;
    }
  }, [externalValue, setValue])

  useEffect(() => {
    if(initialValue)
      setValue(initialValue);
  }, [])

  const handleInput = (value: string): void => {
    setValue(value);
    onChange?.(value);
  };

  const handleSelect = async (address: string): Promise<void> => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onSelect?.({ ...results[0], latLng: { lat, lng }, selectedAddress: address });
    } catch (error) {
      console.log('😱 Error: ', error);
    }
  };

  // Filtrar estados excluidos
  const filteredData = excludedStates.length > 0 
    ? data.filter((suggestion) => {
        let stateCode: string | null = null;
        
        //Buscar en terms por nombre completo de estado
        if (suggestion.terms && Array.isArray(suggestion.terms)) {
          for (const term of suggestion.terms) {
            const stateName = term.value;
            if (STATES[stateName]) {
              stateCode = STATES[stateName];
              break;
            }
          }
        }
        
        //Si no se encontró, intentar buscar código de estado en la descripción
        if (!stateCode) {
          const patterns = [
            /,\s*([A-Z]{2})\s+\d/, // ", CA 90001"
            /,\s*([A-Z]{2}),/,     // ", WA,"
            /,\s*([A-Z]{2})\s*$/,  // ", TX" al final
            /,\s*([A-Z]{2})\s+USA/,// ", NY USA"
          ];
          
          for (const pattern of patterns) {
            const match = suggestion.description.match(pattern);
            if (match) {
              stateCode = match[1];
              break;
            }
          }
        }
        
        if (!stateCode) return true;
        
        return !excludedStates.includes(stateCode);
      })
    : data;

  const options = status === 'OK' ? filteredData.map(({ place_id, description }) => ({
    value: description,
    label: description,
  })) : [];

  // For native mode, use a separate local state to avoid conflicts
  const [localValue, setLocalValue] = useState<string>(externalValue !== undefined ? String(externalValue) : (value || ''));

  // Sync with externalValue when it changes (for native mode)
  useEffect(() => {
    if (useNative && externalValue !== undefined && String(externalValue) !== localValue) {
      setLocalValue(String(externalValue));
      setValue(String(externalValue));
    }
  }, [externalValue, useNative]);

  // Sync with internal value when it changes (but only if externalValue is not set and not in native mode)
  useEffect(() => {
    if (!useNative && externalValue === undefined && value !== localValue) {
      setLocalValue(value);
    }
  }, [value, useNative, externalValue]);

  if (useNative) {
    const handleNativeInput = (inputValue: string): void => {
      setLocalValue(inputValue);
      setValue(inputValue);
      onChange?.(inputValue);
    };

    const handleNativeSelect = async (address: string): Promise<void> => {
      setValue(address, false);
      clearSuggestions();

      try {
        const results = await getGeocode({ address });
        const { lat, lng } = await getLatLng(results[0]);
        const formattedAddress = results[0].formatted_address || address;
        const addressWithoutCountry = formatAddressWithoutCountry(formattedAddress);
        setLocalValue(addressWithoutCountry);
        onSelect?.({ ...results[0], latLng: { lat, lng }, selectedAddress: address });
      } catch (error) {
        console.log('😱 Error: ', error);
        // Still call onSelect even if geocoding fails
        const fallbackAddress = formatAddressWithoutCountry(address);
        setLocalValue(fallbackAddress);
        onSelect?.(address);
      }
    };

    return (
      <div className="autocomplete-address-wrapper">
        <div className={`autocomplete-address ${className} ${error ? 'error' : ''}`}>
          <input
            placeholder={placeholder}
            value={localValue}
            onChange={(e) => handleNativeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleNativeSelect((e.target as HTMLInputElement).value);
              }
            }}
            disabled={disabled}
            autoComplete="off"
            data-lpignore="true"
            data-form-type="other"
          />
        </div>
        {(status === 'OK' && filteredData.length > 0) && (
          <div className={`autocomplete-dropdown ${className || ''}`}>
            {filteredData.map(({ place_id, description }) => (
              <div
                key={place_id}
                className="autocomplete-item"
                onClick={() => handleNativeSelect(description)}
              >
                {description}
              </div>
            ))}
          </div>
        )}
        {error && <p className='error-message'>{error}</p>}
      </div>
    );
  }

  // Always use the internal value from Places API to allow user typing
  // The externalValue is only used to initialize/sync when it changes externally
  const displayValue = value;

  return (
    <div className="autocomplete-address-wrapper">
      <AutoComplete
        value={displayValue}
        onChange={handleInput}
        onSelect={handleSelect}
        options={options}
        disabled={disabled}
        style={{ width: '100%' }}
        className={`autocomplete-address ${className} ${error ? 'error' : ''}`}
        classNames={{ popup: { root: `${className ?? ''} ${SCROLL_FEEDBACK_CLASS}`.trim() } }}
        virtual={false}
        onOpenChange={setIsDropdownOpen}
      >
        <Input.Search
          placeholder={placeholder}
          enterButton={null}
          className="bold-lith"
          disabled={disabled}
        />
      </AutoComplete>
      {error && <p className='error-message'>{error}</p>}
    </div>
  );
}